import { useSyncExternalStore } from 'react';

import type { Apollo } from 'src/gql/generated';
import type { UserSubset } from 'src/types';

import type { PanelVariableType } from './VariableRow';

export type FocusedVariable = Pick<PanelVariableType, 'id' | 'nodeId' | '__typename'>;
type FocusedSignatureField = Pick<Apollo.SignatureField, 'id' | 'nodeId' | '__typename'>;
/**
 * Focus equality on canned fields is compared by `nodeId` — that is the key
 * both Slate/PDF widgets and the DB row carry. `id` may hold the row PK
 * (when focus was set from the Outline panel) or a mirror of `nodeId` (when
 * it came from an editor selection with no PK in reach); never rely on it.
 * `placementNodeId` records *which* placement the focus came from, for the
 * PDF viewer where there is no caret to read it back off. Absent when the
 * focus was set from the panel rather than from a widget.
 */
type FocusedCannedField = {
  __typename: 'CannedField';
  id: UUID;
  nodeId: UUID;
  placementNodeId?: UUID;
};
export type FocusedField = FocusedVariable | FocusedSignatureField | FocusedCannedField;

/** An unfilled role. Role names repeat, so `id` is the placeholder's permission id. */
export interface RoleFilterItem {
  __typename: 'RoleFilterItem';
  avatarColor: string | null;
  id: UUID;
  roleName: string;
}
export type VariableFilterItem = UserSubset | RoleFilterItem;

export interface VariablePanelState {
  filterItems: VariableFilterItem[];
  focusedField: FocusedField | null;
}

const defaultVariablePanelState: VariablePanelState = {
  filterItems: [],
  focusedField: null,
};

const stateByTabId = new Map<UUID, VariablePanelState>();
const listenersByTabId = new Map<UUID, Set<() => void>>();

export function getVariablePanelState(tabId: UUID): VariablePanelState {
  return stateByTabId.get(tabId) ?? defaultVariablePanelState;
}

export function setVariablePanelStateForTabId(
  tabId: UUID,
  state: Partial<VariablePanelState>
): void {
  stateByTabId.set(tabId, { ...getVariablePanelState(tabId), ...state });
  emitVariablePanelStateChange(tabId);
}

export function clearVariablePanelState(tabId: UUID): void {
  stateByTabId.delete(tabId);
  emitVariablePanelStateChange(tabId);
}

export function useVariablePanelStateForTabId(tabId: UUID): VariablePanelState {
  return useSyncExternalStore(
    listener => subscribeToVariablePanelState(tabId, listener),
    () => getVariablePanelState(tabId),
    () => defaultVariablePanelState
  );
}

export function getVariablePanelFocusedField(tabId: UUID): FocusedField | null {
  return getVariablePanelState(tabId).focusedField;
}

export function setVariablePanelFocusedField(tabId: UUID, focusedField: FocusedField | null): void {
  setVariablePanelStateForTabId(tabId, { focusedField });
}

export function clearVariablePanelFocusedField(tabId: UUID): void {
  setVariablePanelFocusedField(tabId, null);
}

export function useVariablePanelFocusedField(tabId: UUID): FocusedField | null {
  return useSyncExternalStore(
    listener => subscribeToVariablePanelState(tabId, listener),
    () => getVariablePanelFocusedField(tabId),
    () => null
  );
}

function emitVariablePanelStateChange(tabId: UUID) {
  listenersByTabId.get(tabId)?.forEach(listener => listener());
}

function subscribeToVariablePanelState(tabId: UUID, listener: () => void) {
  const listeners = getListenersForTabId(tabId);
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
    if (!listeners.size) listenersByTabId.delete(tabId);
  };
}

function getListenersForTabId(tabId: UUID) {
  let listeners = listenersByTabId.get(tabId);
  if (!listeners) {
    listeners = new Set();
    listenersByTabId.set(tabId, listeners);
  }
  return listeners;
}
